import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import {
  reportRequestSchema,
  INCLUDE_SECTIONS,
  INCLUDE_SECTION_VALUES,
  REPORT_PROVIDERS,
  TENDER_TYPES,
} from './shared/schema';
import { BarChart3, Eraser, SlidersHorizontal } from 'lucide-react';
import DateRangePicker from './date-range-picker';
import { getProviderDisplayName } from './lib/ordering';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import instance from 'services/api';

export default function ReportForm({
  onSubmit,
  isLoading,
  setReportData,
  showIncludeModifierOption = false,
  showProviders = true,
  showPaymentMethods = false,
  showSections = true,
  excludedIncludeSections = [],
  includeSectionLabels = {},
  useSavedConfig = true,
}) {
  const [providersPopoverOpen, setProvidersPopoverOpen] = useState(false);
  const [paymentMethodsPopoverOpen, setPaymentMethodsPopoverOpen] =
    useState(false);
  const [includePopoverOpen, setIncludePopoverOpen] = useState(false);
  // Bumped on clear so the picker re-reads the reset dates instead of keeping
  // the range it was showing before.
  const [datePickerKey, setDatePickerKey] = useState(0);
  const currentBusiness = useSelector(
    ({ business }) => business.currentBusiness
  );
  const excludedIncludeSectionsSet = new Set(excludedIncludeSections);
  const availableIncludeSections = INCLUDE_SECTIONS.filter(
    ({ value }) => !excludedIncludeSectionsSet.has(value)
  ).map((section) => ({
    ...section,
    label: includeSectionLabels[section.value] ?? section.label,
  }));
  const availableIncludeSectionValues = INCLUDE_SECTION_VALUES.filter(
    (value) => !excludedIncludeSectionsSet.has(value)
  );
  const getAllowedIncludeSections = (values = []) =>
    values.filter((value) => !excludedIncludeSectionsSet.has(value));

  const [defaultInclude, setDefaultInclude] = useState([
    ...availableIncludeSectionValues,
  ]);
  const configLoaded = useRef(false);

  const getDefaultDateRange = () => {
    const today = new Date();

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(today),
      endDate: formatDate(today),
    };
  };

  const defaultDates = getDefaultDateRange();

  const form = useForm({
    resolver: zodResolver(reportRequestSchema),
    defaultValues: {
      businessUuid: currentBusiness?.uuid,
      businessId: currentBusiness?.id,
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      startTime: '00:00',
      endTime: '23:59',
      filter: 'all',
      useLocalTime: false,
      includeModifierOption: false,
      providers: [...REPORT_PROVIDERS],
      tenderTypes: [],
      include: [...availableIncludeSectionValues],
    },
  });

  useEffect(() => {
    if (!useSavedConfig) return;
    if (configLoaded.current) return;
    configLoaded.current = true;
    instance
      .get('/business')
      .then(({ data: { data } }) => {
        const saved = data?.settings?.posReportConfig?.include;
        if (Array.isArray(saved) && saved.length > 0) {
          const valid = saved.filter((v) =>
            availableIncludeSectionValues.includes(v)
          );
          if (valid.length > 0) {
            setDefaultInclude(valid);
            form.setValue('include', valid);
          }
        }
      })
      .catch(() => {});
  }, [form, useSavedConfig]);

  const handleSubmit = (data) => {
    const submitData = {
      ...data,
      businessUuid: currentBusiness?.uuid,
      businessId: currentBusiness?.id,
      include: getAllowedIncludeSections(data.include),
    };
    if (!submitData.include || submitData.include.length === 0) {
      delete submitData.include;
    }
    if (
      !showPaymentMethods ||
      !submitData.tenderTypes ||
      submitData.tenderTypes.length === 0
    ) {
      delete submitData.tenderTypes;
    }
    onSubmit(submitData);
  };

  const handleClear = () => {
    form.reset({
      businessUuid: currentBusiness?.uuid,
      businessId: currentBusiness?.id,
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      startTime: '00:00',
      endTime: '23:59',
      filter: 'all',
      useLocalTime: false,
      includeModifierOption: false,
      providers: [...REPORT_PROVIDERS],
      tenderTypes: [],
      include: [...defaultInclude],
    });
    setDatePickerKey((value) => value + 1);
    setReportData?.(null);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        {/* Hidden Fields */}
        <FormField
          control={form.control}
          name="businessUuid"
          render={({ field }) => (
            <div className="hidden">
              <FormItem>
                <FormLabel>
                  Business UUID <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="b9005d25-f08a-4fe8-9c08-2c6ce537103d"
                    {...field}
                    data-testid="input-business-uuid"
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Enter your business identifier
                </p>
                <FormMessage />
              </FormItem>
            </div>
          )}
        />

        <FormField
          control={form.control}
          name="businessId"
          render={({ field }) => (
            <div className="hidden">
              <FormItem>
                <FormLabel>Business ID</FormLabel>
                <FormControl>
                  <Input
                    placeholder="5fa2ea8d814815dfe71b272f"
                    {...field}
                    data-testid="input-business-id"
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Optional - for business header info
                </p>
                <FormMessage />
              </FormItem>
            </div>
          )}
        />

        <FormField
          control={form.control}
          name="filter"
          render={({ field }) => (
            <div className="hidden">
              <FormItem>
                <FormLabel>Order Filter</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger data-testid="select-filter">
                      <SelectValue placeholder="Select filter" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="paid">Paid Orders Only</SelectItem>
                    <SelectItem value="all">All Orders</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            </div>
          )}
        />

        {/* Date, Providers, Include Modifiers, and Buttons in row direction */}
        <div className="flex flex-wrap gap-4 items-end">
          {/* Date & Time Range Column */}
          <div className="flex-1 flex flex-col min-w-[260px]">
            <Label className="text-sm font-medium mb-2 whitespace-nowrap">
              Date & Time Range <span className="text-destructive">*</span>
            </Label>
            <div className="[&_button]:h-[44px]">
              <DateRangePicker
                key={datePickerKey}
                initialStartDate={form.watch('startDate')}
                initialEndDate={form.watch('endDate')}
                initialStartTime={form.watch('startTime')}
                initialEndTime={form.watch('endTime')}
                initialUseLocalTime={form.watch('useLocalTime')}
                onDateRangeChange={(startDate, endDate) => {
                  form.setValue('startDate', startDate);
                  form.setValue('endDate', endDate);
                }}
                onTimeRangeChange={(startTime, endTime) => {
                  form.setValue('startTime', startTime);
                  form.setValue('endTime', endTime);
                }}
                onLocalTimeChange={(useLocalTime) => {
                  form.setValue('useLocalTime', useLocalTime);
                }}
                onApply={() => {
                  // Let the picker's values settle in the form before running it.
                  setTimeout(() => {
                    form.handleSubmit(handleSubmit)();
                  }, 0);
                }}
                align="start"
              />
            </div>
          </div>

          {/* Providers Column */}
          {showProviders && (
            <div className="flex-1 flex flex-col min-w-[180px]">
              <FormField
                control={form.control}
                name="providers"
                render={({ field }) => {
                  const selected = new Set(field.value || []);
                  const toggle = (val) => {
                    const next = new Set(selected);
                    if (next.has(val)) {
                      next.delete(val);
                    } else {
                      next.add(val);
                    }
                    field.onChange(Array.from(next));
                  };

                  // Preset handlers
                  const applyPreset = (preset) => {
                    if (preset === 'all') {
                      field.onChange([...REPORT_PROVIDERS]);
                      return;
                    }
                    if (preset === 'all_third_party') {
                      field.onChange([]);
                      return;
                    }
                    if (preset === 'all_first_party') {
                      field.onChange([...REPORT_PROVIDERS]);
                      return;
                    }
                    field.onChange([]);
                  };

                  const providerOptions = REPORT_PROVIDERS.map((id) => ({
                    id,
                    label: getProviderDisplayName(id),
                  }));

                  return (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-sm font-medium whitespace-nowrap">
                        Providers
                      </FormLabel>
                      <FormControl>
                        <Popover
                          open={providersPopoverOpen}
                          onOpenChange={setProvidersPopoverOpen}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between font-normal h-11"
                            >
                              <span className="truncate">
                                {field.value && field.value.length > 0
                                  ? field.value
                                      .map((v) => getProviderDisplayName(v))
                                      .join(', ')
                                  : 'Select providers'}
                              </span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-80">
                            <Command>
                              <CommandInput placeholder="Search providers..." />
                              <CommandList>
                                <CommandEmpty>No results found.</CommandEmpty>
                                <CommandGroup heading="Presets">
                                  <CommandItem
                                    onSelect={() => applyPreset('all')}
                                  >
                                    All providers
                                  </CommandItem>
                                  <CommandItem disabled>
                                    <span className="text-muted-foreground">
                                      All 3rd party providers (coming soon)
                                    </span>
                                  </CommandItem>
                                  <CommandItem disabled>
                                    <span className="text-muted-foreground">
                                      All 1st party providers (coming soon)
                                    </span>
                                  </CommandItem>
                                </CommandGroup>
                                <CommandGroup heading="Providers">
                                  {providerOptions.map((opt) => (
                                    <CommandItem
                                      key={opt.id}
                                      onSelect={() => toggle(opt.id)}
                                    >
                                      <div className="mr-2">
                                        <Checkbox
                                          checked={selected.has(opt.id)}
                                          onCheckedChange={() => toggle(opt.id)}
                                        />
                                      </div>
                                      {opt.label}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                            <div className="p-2 border-t">
                              <Button
                                type="button"
                                className="w-full"
                                onClick={() => {
                                  setProvidersPopoverOpen(false);
                                  form.handleSubmit(handleSubmit)();
                                }}
                              >
                                Done
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          )}

          {/* Payment Methods Column */}
          {showPaymentMethods && (
            <div className="flex-1 flex flex-col min-w-[180px]">
              <FormField
                control={form.control}
                name="tenderTypes"
                render={({ field }) => {
                  // An empty selection is "All": the request carries no
                  // tender filter. Ticking every type collapses back to it.
                  const selected = new Set(field.value || []);
                  const isAll = selected.size === 0;

                  const toggle = (val) => {
                    const next = new Set(selected);
                    if (next.has(val)) {
                      next.delete(val);
                    } else {
                      next.add(val);
                    }
                    field.onChange(
                      next.size === TENDER_TYPES.length ? [] : Array.from(next)
                    );
                  };

                  const summaryLabel = isAll
                    ? 'All payment methods'
                    : TENDER_TYPES.filter(({ value }) => selected.has(value))
                        .map(({ label }) => label)
                        .join(', ');

                  return (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-sm font-medium whitespace-nowrap">
                        Payment Method
                      </FormLabel>
                      <FormControl>
                        <Popover
                          open={paymentMethodsPopoverOpen}
                          onOpenChange={setPaymentMethodsPopoverOpen}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between font-normal h-11"
                            >
                              <span className="truncate">{summaryLabel}</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-64" align="start">
                            <div className="p-2">
                              <label className="flex items-center gap-2 px-1 py-1.5 rounded hover:bg-muted cursor-pointer">
                                <Checkbox
                                  checked={isAll}
                                  onCheckedChange={() => field.onChange([])}
                                />
                                <span className="text-sm">All</span>
                              </label>
                              {TENDER_TYPES.map((item) => (
                                <label
                                  key={item.value}
                                  className="flex items-center gap-2 px-1 py-1.5 rounded hover:bg-muted cursor-pointer"
                                >
                                  <Checkbox
                                    checked={selected.has(item.value)}
                                    onCheckedChange={() => toggle(item.value)}
                                  />
                                  <span className="text-sm">{item.label}</span>
                                </label>
                              ))}
                            </div>
                            <div className="p-2 border-t">
                              <Button
                                type="button"
                                className="w-full"
                                size="sm"
                                onClick={() => {
                                  setPaymentMethodsPopoverOpen(false);
                                  form.handleSubmit(handleSubmit)();
                                }}
                              >
                                Done
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          )}

          {/* Include Sections Column */}
          {showSections && (
            <div className="flex-1 flex flex-col min-w-[180px]">
              <FormField
                control={form.control}
                name="include"
                render={({ field }) => {
                  const allItems = availableIncludeSections;
                  const selected = new Set(
                    getAllowedIncludeSections(field.value || [])
                  );
                  const selectedCount = selected.size;
                  const totalCount = allItems.length;
                  const isAllSelected = selectedCount === totalCount;

                  const getSummaryLabel = () => {
                    if (selectedCount === 0) return 'Select sections';
                    if (selectedCount === totalCount) return 'All sections';
                    if (selectedCount <= 2) {
                      return allItems
                        .filter((i) => selected.has(i.value))
                        .map((i) => i.label)
                        .join(', ');
                    }
                    return `${selectedCount} of ${totalCount} sections`;
                  };

                  const toggle = (val) => {
                    const next = new Set(selected);
                    if (next.has(val)) {
                      next.delete(val);
                    } else {
                      next.add(val);
                    }
                    field.onChange(Array.from(next));
                  };

                  return (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-sm font-medium">
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <SlidersHorizontal className="h-3 w-3" />
                          Sections
                          {!isAllSelected && (
                            <button
                              type="button"
                              className="ml-1 text-xs text-primary underline hover:no-underline font-normal"
                              onClick={(e) => {
                                e.preventDefault();
                                field.onChange([
                                  ...availableIncludeSectionValues,
                                ]);
                              }}
                            >
                              all
                            </button>
                          )}
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Popover
                          open={includePopoverOpen}
                          onOpenChange={setIncludePopoverOpen}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between font-normal h-11 min-w-0"
                            >
                              <span className="truncate block w-full text-left">
                                {getSummaryLabel()}
                              </span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="p-0 w-72"
                            align="start"
                            style={{ maxHeight: '50vh' }}
                          >
                            <div className="flex gap-1 p-2 border-b">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs flex-1"
                                onClick={() =>
                                  field.onChange([
                                    ...availableIncludeSectionValues,
                                  ])
                                }
                              >
                                All
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs flex-1"
                                onClick={() =>
                                  field.onChange([...defaultInclude])
                                }
                              >
                                Default
                              </Button>
                            </div>
                            <div
                              className="overflow-y-auto p-2"
                              style={{ maxHeight: 'calc(50vh - 90px)' }}
                            >
                              {availableIncludeSections.map((item) => (
                                <label
                                  key={item.value}
                                  className="flex items-center gap-2 px-1 py-1.5 rounded hover:bg-muted cursor-pointer"
                                >
                                  <Checkbox
                                    checked={selected.has(item.value)}
                                    onCheckedChange={() => toggle(item.value)}
                                  />
                                  <span className="text-sm">{item.label}</span>
                                </label>
                              ))}
                            </div>
                            <div className="p-2 border-t">
                              <Button
                                type="button"
                                className="w-full"
                                size="sm"
                                onClick={() => setIncludePopoverOpen(false)}
                              >
                                Done
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          )}

          {/* Include Modifiers Checkbox - Only for Top Seller Items */}
          {showIncludeModifierOption && (
            <div className="flex-1 flex flex-col min-w-[150px]">
              <Label className="text-sm font-medium mb-2 whitespace-nowrap">
                Include Modifiers
              </Label>
              <FormField
                control={form.control}
                name="includeModifierOption"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-center space-y-0 h-11 border rounded-md bg-white">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Buttons Column - Aligned with input controls */}
          <div className="flex flex-col flex-shrink-0">
            {/* Invisible label to match exact height and spacing */}
            <Label className="text-sm font-medium mb-2 invisible">
              Actions
            </Label>
            <div className="flex items-center space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClear}
                disabled={isLoading}
                data-testid="button-clear"
                className="h-11 whitespace-nowrap"
              >
                <Eraser className="mr-2 h-4 w-4" />
                Clear
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                data-testid="button-generate"
                className="h-11 flex items-center justify-center whitespace-nowrap"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <BarChart3 className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
